// The Laptops page's reading of GET /laptops, kept out of the page so it can
// be run in a test rather than matched as source.

export const ROSTER_FILTERS = [
  { id: 'all',       label: 'Everyone' },
  { id: 'missing',   label: 'No laptop' },
  { id: 'suggested', label: 'Suggested by PDQ' },
  { id: 'flagged',   label: 'Needs a look' },
  { id: 'exempt',    label: 'Not needed' },
];

export const STATUS_LABEL = {
  assigned: 'Assigned',
  suggested: 'Suggested',
  missing: 'No laptop',
  exempt: 'Not needed',
};

export const displayName = (p) => `${p.preferred_name || p.first_name} ${p.last_name}`;

const haystack = (p) => [
  p.first_name, p.last_name, p.preferred_name, p.team_name, p.designation,
  p.laptop?.asset_tag, p.laptop?.serial_number, p.laptop?.model, p.suggestion?.name,
].filter(Boolean).join(' ').toLowerCase();

/** The roster rows to show for `filter` and `search`. */
export function filterRoster(rows, filter = 'all', search = '') {
  const q = (search || '').trim().toLowerCase();
  return rows.filter(p => {
    if (filter === 'missing' && p.status !== 'missing' && p.status !== 'suggested') return false;
    if (filter === 'suggested' && p.status !== 'suggested') return false;
    if (filter === 'flagged' && !(p.flags || []).length) return false;
    if (filter === 'exempt' && p.status !== 'exempt') return false;
    if (q && !haystack(p).includes(q)) return false;
    return true;
  });
}

/**
 * The PDQ laptops to offer when assigning one to `personId`: theirs by PDQ
 * first, then nobody's, then somebody else's (moving those asks first).
 */
export function pickerGroups(devices, personId) {
  const usedByThem = [];
  const free = [];
  const held = [];
  for (const d of devices || []) {
    if (d.holder?.id === personId) continue;
    if (d.holder) held.push(d);
    else if (d.user?.id === personId) usedByThem.push(d);
    else free.push(d);
  }
  const byName = (a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { numeric: true });
  return [
    { label: 'Signed in to by this person', devices: usedByThem.sort(byName) },
    { label: 'Not assigned to anybody', devices: free.sort(byName) },
    { label: 'Assigned to somebody else', devices: held.sort(byName) },
  ].filter(g => g.devices.length);
}

/** The sync line under the title: when PDQ last answered, or why it did not. */
export function syncLine(pdq) {
  if (!pdq?.configured) return { kind: 'off', text: 'PDQ Connect is not connected. Assign laptops by asset tag; suggestions appear once the PDQ_API_KEY secret is set.' };
  const s = pdq.last_sync;
  if (!s) return { kind: 'pending', text: 'PDQ Connect is connected but has not synced yet.' };
  if (!s.ok) return { kind: 'error', at: s.at, text: `The last PDQ sync failed: ${s.error}` };
  return { kind: 'ok', at: s.at, text: `${s.devices} device${s.devices === 1 ? '' : 's'} from PDQ Connect` };
}

// ── Unassigned laptops ───────────────────────────────────────────────────────

export const STOCK_LABEL = {
  unsorted: 'Not sorted',
  spare: 'Spare',
  repair: 'In repair',
  retired: 'Retired',
};

/** The states a person can choose for an unassigned laptop, in order. */
export const STOCK_STATES = ['unsorted', 'spare', 'repair', 'retired'];

export const UNASSIGNED_FILTERS = [
  { id: 'open',     label: 'To issue or sort' },
  { id: 'unsorted', label: 'Not sorted' },
  { id: 'spare',    label: 'Spare' },
  { id: 'repair',   label: 'In repair' },
  { id: 'retired',  label: 'Retired' },
];

/** The unassigned laptops to show for `filter`: retired ones only when asked for. */
export function filterUnassigned(rows, filter = 'open') {
  return (rows || []).filter(u => (filter === 'open' ? u.state !== 'retired' : u.state === filter));
}

/** Where an unassigned laptop came from, in a line. `fmt` formats a date. */
export function unassignedOrigin(u, fmt = (d) => d) {
  if (u.returned) {
    return `Returned${u.returned.person_name ? ` by ${u.returned.person_name}` : ''}${u.returned.returned_date ? ` on ${fmt(u.returned.returned_date)}` : ''}`;
  }
  if (u.source === 'added') return 'Added here';
  if (u.user) return `In PDQ, last signed in to by ${u.user.name}`;
  return 'In PDQ, not assigned on Ops';
}

/**
 * The people to offer when giving out an unassigned laptop: those with no
 * laptop first, then the rest (whose laptop would be recorded as returned).
 * Somebody marked as not needing one is offered with the rest.
 */
export function assigneeGroups(people) {
  const byName = (a, b) => displayName(a).localeCompare(displayName(b));
  const without = (people || []).filter(p => p.status === 'missing' || p.status === 'suggested').sort(byName);
  const rest = (people || []).filter(p => p.status !== 'missing' && p.status !== 'suggested').sort(byName);
  return [
    { label: 'No laptop', people: without },
    { label: 'Has a laptop or does not need one', people: rest },
  ].filter(g => g.people.length);
}
